/**
 * Smart Search Bar for the Modern Dialer (Phase A).
 *
 * Instant client-side search across:
 *   • Saved contacts (name, phone, country)
 *   • Countries / destinations (name, dialing code)
 *   • Recent dialled numbers (phone, contact name)
 *
 * Designed as a controlled component — parent owns the input state and the
 * "pick a result" handler. Results dropdown is portaled below the input via
 * absolute positioning (no react-portal dep needed).
 */
import { useMemo, useRef, useEffect } from "react";
import { Search, X, User, Globe, Clock } from "lucide-react";
import { flagFor } from "@/components/customer/CountryPicker";

const MAX_RESULTS = 8;

export default function DialerSmartSearch({
  value,
  onChange,
  contacts = [],
  countries = [],
  recents = [],
  onPickContact,
  onPickCountry,
  onPickRecent,
  placeholder = "Search contacts, countries, recents…",
}) {
  const inputRef = useRef(null);

  // Auto-focus only when explicitly toggled on; ignored here for now.
  useEffect(() => { /* no-op placeholder for future keyboard shortcut */ }, []);

  const needle = (value || "").trim().toLowerCase();
  const open = needle.length >= 1;

  const results = useMemo(() => {
    if (!open) return { contacts: [], countries: [], recents: [] };
    const matchesString = (s) => (s || "").toLowerCase().includes(needle);
    const cleanNum = needle.replace(/[^\d+]/g, "");

    const c = contacts
      .filter((x) => matchesString(x.name) || matchesString(x.phone_e164) || (cleanNum && (x.phone_e164 || "").includes(cleanNum)))
      .slice(0, MAX_RESULTS);

    const co = countries
      .filter((x) => matchesString(x.country_name) || matchesString(x.country_code) || (cleanNum && x.prefix.includes(cleanNum.replace("+", ""))))
      .slice(0, MAX_RESULTS);

    const r = recents
      .filter((x) => matchesString(x.contact_name) || matchesString(x.phone_e164) || (cleanNum && (x.phone_e164 || "").includes(cleanNum)))
      .slice(0, MAX_RESULTS);

    return { contacts: c, countries: co, recents: r };
  }, [open, needle, contacts, countries, recents]);

  const empty = open && !results.contacts.length && !results.countries.length && !results.recents.length;

  return (
    <div className="relative" data-testid="dialer-smart-search">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted pointer-events-none" />
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid="dialer-search-input"
        className="w-full rounded-2xl bg-white border border-brand-line pl-10 pr-9 py-3 text-sm font-medium text-brand-ink placeholder:text-brand-muted focus:border-brand-green focus:outline-none focus:ring-4 focus:ring-brand-green/15 transition"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          data-testid="dialer-search-clear"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-brand-muted hover:bg-brand-bgSoft"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {open && (
        <div
          className="absolute z-30 left-0 right-0 mt-2 rounded-2xl border border-brand-line bg-white shadow-[0_18px_42px_-18px_rgba(7,27,77,0.35)] max-h-[60vh] overflow-y-auto"
          data-testid="dialer-search-results"
        >
          {empty && (
            <p className="px-4 py-6 text-center text-xs text-brand-muted" data-testid="dialer-search-empty">
              No matches. Keep typing or pick a number from the keypad.
            </p>
          )}

          {results.contacts.length > 0 && (
            <Section icon={<User className="h-3 w-3" />} title="Contacts">
              {results.contacts.map((c) => (
                <ResultRow
                  key={`c-${c.id}`}
                  testId={`search-contact-${c.id}`}
                  onClick={() => onPickContact?.(c)}
                  primary={c.name}
                  secondary={c.phone_e164}
                  trailing={c.country ? flagFor(c.country) : null}
                />
              ))}
            </Section>
          )}

          {results.recents.length > 0 && (
            <Section icon={<Clock className="h-3 w-3" />} title="Recents">
              {results.recents.map((r) => (
                <ResultRow
                  key={`r-${r.phone_e164}`}
                  testId={`search-recent-${r.phone_e164}`}
                  onClick={() => onPickRecent?.(r)}
                  primary={r.contact_name || r.phone_e164}
                  secondary={r.contact_name ? r.phone_e164 : (r.destination || "—")}
                />
              ))}
            </Section>
          )}

          {results.countries.length > 0 && (
            <Section icon={<Globe className="h-3 w-3" />} title="Destinations">
              {results.countries.map((c) => (
                <ResultRow
                  key={`co-${c.country_code}`}
                  testId={`search-country-${c.country_code}`}
                  onClick={() => onPickCountry?.(c)}
                  primary={c.country_name}
                  secondary={`+${c.prefix} · £${c.rate_per_minute.toFixed(3)}/min`}
                  trailing={<span className="text-lg leading-none">{flagFor(c.country_code)}</span>}
                />
              ))}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="py-1.5">
      <div className="px-4 pt-1 pb-1 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-brand-muted">
        {icon}{title}
      </div>
      <ul className="divide-y divide-brand-line/60">{children}</ul>
    </div>
  );
}

function ResultRow({ onClick, primary, secondary, trailing, testId }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-brand-bgSoft active:bg-brand-greenSoft transition"
      >
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-brand-ink truncate">{primary}</p>
          <p className="text-[11px] text-brand-muted truncate font-mono">{secondary}</p>
        </div>
        {trailing && <span className="shrink-0 ml-2" aria-hidden>{trailing}</span>}
      </button>
    </li>
  );
}
